// CB:BEGIN operations-control-plane-ui
/**
 * Admin Operations: search (project id, address, homeowner/contractor email, escrow/invoice id) and project timeline.
 * Also lists all active (non-terminal) projects.
 * Goal: answer "What happened on project X?" in under 60 seconds.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useApi } from '../../lib/useApi';

interface SearchProject {
  id: string;
  address: string;
  status: string;
  createdAt: string;
  homeownerId: string | null;
}

/** Terminal statuses – projects in these states are excluded from "active" list */
const TERMINAL_STATUSES = new Set(['CANCELLED', 'COMPLETED', 'CLOSED', 'DISPUTED', 'OUT_OF_SCOPE']);

interface ListProject {
  id: string;
  address: string;
  status: string;
  createdAt: string;
  homeowner?: { firstName?: string; lastName?: string; user?: { email?: string } };
  _count?: { bids: number };
}

interface SearchResult {
  ok: boolean;
  query: string;
  projects: SearchProject[];
  escrows: unknown[];
  invoices: unknown[];
}

interface TimelineItem {
  at: string;
  type: string;
  summary: string;
  payload?: Record<string, unknown>;
}

interface TimelineResult {
  ok: boolean;
  project: { id: string; address: string; status: string };
  timeline: TimelineItem[];
}

interface AdminProjectsResponse {
  projects: ListProject[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

const AdminOperationsPage: React.FC = () => {
  const api = useApi();
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<TimelineResult | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState<string | null>(null);
  // CB:BEGIN admin-operations-active-projects
  const [activeProjects, setActiveProjects] = useState<ListProject[]>([]);
  const [activeProjectsLoading, setActiveProjectsLoading] = useState(true);
  const [activeProjectsError, setActiveProjectsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setActiveProjectsError(null);
      setActiveProjectsLoading(true);
      try {
        const data = await api.get<AdminProjectsResponse>('/api/admin/projects?limit=200&page=1');
        if (cancelled) return;
        const active = (data.projects || []).filter((p) => !TERMINAL_STATUSES.has(p.status));
        setActiveProjects(active);
      } catch (e) {
        if (!cancelled) {
          setActiveProjectsError(e instanceof Error ? e.message : 'Failed to load active projects');
          setActiveProjects([]);
        }
      } finally {
        if (!cancelled) setActiveProjectsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api]);
  // CB:END admin-operations-active-projects

  const onSearch = useCallback(async () => {
    const q = query.trim();
    if (q.length < 2) {
      setSearchError('Enter at least 2 characters');
      return;
    }
    setSearchError(null);
    setSearchResults(null);
    setSearchLoading(true);
    try {
      const data = await api.get<SearchResult>(`/api/admin/search?q=${encodeURIComponent(q)}&limit=20`);
      setSearchResults(data);
    } catch (e) {
      setSearchError(e instanceof Error ? e.message : 'Search failed');
      setSearchResults(null);
    } finally {
      setSearchLoading(false);
    }
  }, [api, query]);

  const loadTimeline = useCallback(async (projectId: string) => {
    setSelectedProjectId(projectId);
    setTimelineError(null);
    setTimeline(null);
    setTimelineLoading(true);
    try {
      const data = await api.get<TimelineResult>(`/api/admin/projects/${projectId}/timeline`);
      setTimeline(data);
    } catch (e) {
      setTimelineError(e instanceof Error ? e.message : 'Failed to load timeline');
      setTimeline(null);
    } finally {
      setTimelineLoading(false);
    }
  }, [api]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="admin-operations-page">
      <h1 className="text-2xl font-semibold text-gray-900 mb-2">Operations</h1>
      <p className="text-gray-600 mb-6">
        Search by project id, address, homeowner or contractor email, escrow id, or invoice id. Open a project to see its timeline.
      </p>

      {/* CB:BEGIN admin-operations-active-projects */}
      <section className="mb-8">
        <h2 className="text-lg font-medium text-gray-800 mb-2">Active projects</h2>
        {activeProjectsError && <p className="text-red-600 text-sm mb-2" role="alert">{activeProjectsError}</p>}
        {activeProjectsLoading && <p className="text-gray-500 text-sm">Loading…</p>}
        {!activeProjectsLoading && !activeProjectsError && (
          activeProjects.length === 0 ? (
            <p className="text-gray-500 text-sm">No active projects.</p>
          ) : (
            <ul className="border border-gray-200 rounded divide-y divide-gray-200 max-h-[320px] overflow-y-auto">
              {activeProjects.map((p) => (
                <li key={p.id} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50">
                  <div className="min-w-0">
                    <span className="font-mono text-xs text-gray-500 block truncate">{p.id}</span>
                    <span className="font-medium text-gray-800 truncate block">{p.address}</span>
                    <span className="text-gray-500 text-sm">{p.status}</span>
                    {p.homeowner?.user?.email && (
                      <span className="text-gray-400 text-xs block truncate">{p.homeowner.user.email}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => loadTimeline(p.id)}
                    className="text-sky-600 text-sm font-medium shrink-0 ml-2"
                    data-testid={`admin-operations-open-timeline-${p.id}`}
                  >
                    Timeline
                  </button>
                </li>
              ))}
            </ul>
          )
        )}
      </section>
      {/* CB:END admin-operations-active-projects */}

      <div className="flex gap-2 mb-6">
        <input
          type="text"
          placeholder="Project ID, address, email, escrow ID, invoice ID…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onSearch()}
          className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm"
          data-testid="admin-operations-search-input"
        />
        <button
          type="button"
          onClick={onSearch}
          disabled={searchLoading}
          className="rounded bg-sky-600 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
          data-testid="admin-operations-search-btn"
        >
          {searchLoading ? 'Searching…' : 'Search'}
        </button>
      </div>

      {searchError && <p className="text-red-600 text-sm mb-4" role="alert">{searchError}</p>}

      {searchResults && (
        <section className="mb-8">
          <h2 className="text-lg font-medium text-gray-800 mb-2">Results</h2>
          {searchResults.projects.length === 0 ? (
            <p className="text-gray-500">No projects found.</p>
          ) : (
            <ul className="border border-gray-200 rounded divide-y divide-gray-200">
              {searchResults.projects.map((p) => (
                <li key={p.id} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50">
                  <div>
                    <span className="font-mono text-xs text-gray-500">{p.id}</span>
                    <span className="ml-2">{p.address}</span>
                    <span className="ml-2 text-gray-500">({p.status})</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => loadTimeline(p.id)}
                    className="text-sky-600 text-sm font-medium"
                    data-testid={`admin-operations-open-timeline-${p.id}`}
                  >
                    Timeline
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {selectedProjectId && (
        <section>
          <h2 className="text-lg font-medium text-gray-800 mb-2">Timeline</h2>
          {timelineError && <p className="text-red-600 text-sm mb-2" role="alert">{timelineError}</p>}
          {timelineLoading && <p className="text-gray-500">Loading timeline…</p>}
          {timeline && !timelineLoading && (
            <div className="border border-gray-200 rounded overflow-hidden">
              <div className="bg-gray-100 px-3 py-2 text-sm text-gray-700">
                {timeline.project.address} — {timeline.project.status}
              </div>
              <ul className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
                {timeline.timeline.length === 0 ? (
                  <li className="px-3 py-4 text-gray-500 text-sm">No events yet.</li>
                ) : (
                  timeline.timeline.map((item, i) => (
                    <li key={i} className="px-3 py-2 flex gap-3 text-sm">
                      <span className="text-gray-500 shrink-0">
                        {new Date(item.at).toLocaleString()}
                      </span>
                      <span className="font-medium text-gray-700">{item.type}</span>
                      <span className="text-gray-600">{item.summary}</span>
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default AdminOperationsPage;
// CB:END operations-control-plane-ui
